import { useEffect, useMemo, useState } from 'react'
import { Grid, Progress } from 'semantic-ui-react'
import { useNavigate } from 'react-router-dom'
import { DateTime } from 'luxon'
import styled from 'styled-components'

import {
  Text,
  Card,
  Button,
  Icon,
  Link,
  GridRowColumn,
  Accordion,
  Popup,
  Label,
} from '@/base'
import { useAnalyticsTrack } from '@/featuresV1/Amplitude'
import { DATE_FORMATS_LUXON } from '@/utils/dateHelpers'
import { useReselector } from '@/utils/sharedHooks'
import {
  formatCurrency,
  formatCurrencyFromCents,
} from '@/utils/currencyHelpers'
import { light, regular } from '@fortawesome/fontawesome-svg-core/import.macro'
import InsightsTrendGraph from '@/componentsV1/Finances/PracticeInsights/InsightsTrendGraph'
import { fetchInsightsBarchartData } from '@/componentsV1/Finances/shared/financeActions'
import {
  INSIGHTS_TYPE,
  processDataForChart,
} from '@/componentsV1/Finances/shared/utils'
import { useAppDispatch } from '@/utils/typeHelpers'
import { getFinancialProfile } from '@/selectors/user.selectors'
import { Colors } from '@/src/styles/theme'
import {
  selectExpenseRatioGoalDifference,
  selectIncomeGoalPercentage,
  selectProfitGoal,
  selectProfitGoalPercentage,
  selectYtdExpenseRatio,
  selectYtdExpenses,
  selectYtdIncome,
  selectYtdProfit,
} from '@/featuresV1/GoalSetting/goals.selectors'

// Using semantic ui isn't great but there isn't a v1 design component for this and we are migrating to v2 very soon
const CardProgress = styled(Progress)({
  '&&&&&&': {
    flex: 1,
    marginBottom: 15,
    marginTop: 15,

    '.bar': {
      backgroundColor: Colors.green,
      height: 10,
    },
  },
})

const YearToDateProfitLossCard = () => {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const fp = useReselector(getFinancialProfile)
  const [trendData, setTrendData] = useState<
    { [key: string]: string | number | undefined }[]
  >([])
  const track = useAnalyticsTrack()
  const todayDate = useMemo(() => DateTime.now(), [])

  const profitGoalPercentage = useReselector(selectProfitGoalPercentage)

  const ytdIncome = useReselector(selectYtdIncome)
  const ytdExpenses = useReselector(selectYtdExpenses)
  const profitGoal = useReselector(selectProfitGoal)
  const incomeGoalPercentage = useReselector(selectIncomeGoalPercentage)
  const ytdExpenseRatio = useReselector(selectYtdExpenseRatio)
  const expenseRatioDiff = useReselector(selectExpenseRatioGoalDifference)
  const ytdProfit = useReselector(selectYtdProfit)

  useEffect(() => {
    const fetchTrendData = async () => {
      const startDate = todayDate
        .startOf('year')
        .toFormat(DATE_FORMATS_LUXON.TIMESTAMP)
      const endDate = todayDate.toFormat(DATE_FORMATS_LUXON.TIMESTAMP)
      const endOfYear = todayDate
        .endOf('year')
        .toFormat(DATE_FORMATS_LUXON.TIMESTAMP)

      const fetchedData = await fetchInsightsBarchartData(
        INSIGHTS_TYPE.PROFIT,
        startDate,
        endDate
      )(dispatch)

      if (fetchedData) {
        const chartData = processDataForChart(
          fetchedData,
          [startDate, endOfYear],
          INSIGHTS_TYPE.PROFIT
        )
        setTrendData(chartData)
      }
    }
    fetchTrendData()
  }, [dispatch, todayDate])

  return (
    <Card type="section">
      <Grid>
        <Grid.Column width={10}>
          <Text as="h2">Year to Date Profit & Loss</Text>
          <Text as="bodySm" color="darkGray">
            {todayDate
              .startOf('year')
              .toFormat(DATE_FORMATS_LUXON.DISPLAY_LONG)}{' '}
            - {todayDate.toFormat(DATE_FORMATS_LUXON.DISPLAY_LONG)}
          </Text>
        </Grid.Column>
        <Grid.Column
          width={6}
          style={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'flex-end',
          }}
        >
          <Button
            variant="secondaryLink"
            style={{ marginRight: 25 }}
            onClick={() => {
              const action =
                fp?.annualIncomeGoalInCents || fp?.annualExpenseRatioGoal
                  ? 'Edit'
                  : 'Set'
              track(`Clicked ${action} Practice Goals`)
              navigate('/goal-setting')
            }}
          >
            <Icon icon={light('pencil')} style={{ marginRight: 8 }} />
            {fp?.annualIncomeGoalInCents || fp?.annualExpenseRatioGoal
              ? 'Edit Goals'
              : 'Set Goals'}
          </Button>
          <Button
            variant="secondaryLink"
            onClick={() => {
              track('Clicked year to date profiles View Reports Button')
              navigate('/insights/overview')
            }}
          >
            View Reports
            <Icon icon={light('arrow-right')} style={{ marginLeft: 8 }} />
          </Button>
        </Grid.Column>
        <Grid.Row columns="equal">
          <Grid.Column>
            <Card type="subsection">
              <Text as="display2" style={{ marginBottom: 5 }}>
                {formatCurrency(ytdIncome)}
              </Text>
              <Text as="eyebrow" color="darkGray">
                year to date income
                <Popup
                  content={
                    <>
                      <Text as="h3">Year to Date Income</Text>
                      <br />
                      <Text as="bodySm">
                        This is the total income that has been categorized by
                        Heard&apos;s bookkeeping team this calendar year.
                        <br />
                        <br />
                        If you’ve set a goal, the bar graph will track your
                        progress towards your income target that you set.
                        <br />
                        <br />
                        Use the “Edit Targets” button in the upper-right hand
                        corner of Year to Date Profit and Loss to update your
                        target.
                      </Text>
                    </>
                  }
                />
              </Text>
              <br />
              {fp?.annualIncomeGoalInCents ? (
                <div>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <CardProgress percent={incomeGoalPercentage} />
                    <Text color="accentGreen" style={{ marginLeft: 10 }}>
                      {incomeGoalPercentage}%
                    </Text>
                  </div>
                  <Text>
                    Annual Income Goal of{' '}
                    {formatCurrencyFromCents(fp.annualIncomeGoalInCents, {
                      hideDecimalsIfZero: true,
                    })}
                  </Text>
                </div>
              ) : (
                <Button
                  variant="secondary"
                  onClick={() => {
                    track('Clicked Set Income Practice Goal from metric')
                    navigate('/goal-setting')
                  }}
                >
                  <Icon icon={regular('bullseye')} style={{ marginRight: 8 }} />
                  Set Goal
                </Button>
              )}
              <br />
              <Link
                to="/insights/income"
                trackMessage="Clicked year to date income"
                style={{ justifyContent: 'flex-start' }}
              >
                View income breakdown
                <Icon icon={light('arrow-right')} style={{ marginLeft: 8 }} />
              </Link>
            </Card>
          </Grid.Column>
          <Grid.Column>
            <Card type="subsection">
              <Text as="display2" style={{ marginBottom: 5 }}>
                -{formatCurrency(Math.abs(ytdExpenses))}
              </Text>
              <Text as="eyebrow" color="darkGray">
                year to date expenses
                <Popup
                  content={
                    <>
                      <Text as="h3">Year to Date Expenses</Text>
                      <br />
                      <Text as="bodySm">
                        This is the total amount of expenses that has been
                        categorized by Heard&apos;s bookkeeping team this
                        calendar year.
                        <br />
                        <br />
                        If you’ve set a goal, you will see your expense ratio
                        (your expenses as a percentage of your income) for the
                        year compared with your target.
                        <br />
                        <br />
                        Use the “Edit Targets” button in the upper-right hand
                        corner of Year to Date Profit and Loss to update your
                        target.
                      </Text>
                    </>
                  }
                />
              </Text>
              <br />
              {fp?.annualExpenseRatioGoal ? (
                <div>
                  <div
                    style={{ display: 'flex', marginBottom: 5, marginTop: 5 }}
                  >
                    <Text as="h2" style={{ marginRight: 8 }}>
                      {ytdExpenseRatio}%
                    </Text>
                    <Label color={expenseRatioDiff > 10 ? 'orange' : 'gray'}>
                      {expenseRatioDiff > 0 && '+'}
                      {expenseRatioDiff.toFixed(2)}%
                    </Label>
                  </div>
                  <Text>
                    Expense Ratio Goal of {fp.annualExpenseRatioGoal * 100}%
                  </Text>
                </div>
              ) : (
                <Button
                  variant="secondary"
                  onClick={() => {
                    track('Clicked Set Expense Ratio Practice Goal from metric')
                    navigate('/goal-setting')
                  }}
                >
                  <Icon icon={regular('bullseye')} style={{ marginRight: 8 }} />
                  Set Goal
                </Button>
              )}
              <br />
              <Link
                to="/insights/expenses"
                trackMessage="Clicked year to date expenses"
                style={{ justifyContent: 'flex-start' }}
              >
                View expenses breakdown
                <Icon icon={light('arrow-right')} style={{ marginLeft: 8 }} />
              </Link>
            </Card>
          </Grid.Column>
          <Grid.Column>
            <Card type="subsection">
              <Text as="display2" style={{ marginBottom: 5 }}>
                {formatCurrency(ytdProfit, { hideDecimalsIfZero: true })}
              </Text>
              <Text as="eyebrow" color="darkGray">
                year to date profit
                <Popup
                  content={
                    <>
                      <Text as="h3">Year to Date Profit</Text>
                      <br />
                      <Text as="bodySm">
                        This is the total bookkeeper reconciled amount after
                        expenses are paid, or your take-home earnings before
                        taxes for this calendar year.
                        <br />
                        <br />
                        If you’ve set a goal, you will see your profit for the
                        year compared with your target.
                        <br />
                        <br />
                        Use the “Edit Targets” button in the upper-right hand
                        corner of Year to Date Profit and Loss to update your
                        target.
                      </Text>
                    </>
                  }
                />
              </Text>
              <br />
              {fp?.annualIncomeGoalInCents && fp.annualExpenseRatioGoal ? (
                <div>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <CardProgress percent={profitGoalPercentage} />
                    <Text color="accentGreen" style={{ marginLeft: 10 }}>
                      {profitGoalPercentage}%
                    </Text>
                  </div>
                  <Text>
                    Annual Profit Goal of{' '}
                    {formatCurrency(profitGoal, { hideDecimalsIfZero: true })}
                  </Text>
                </div>
              ) : (
                <Button
                  variant="secondary"
                  onClick={() => {
                    track('Clicked Set Profit Practice Goal from metric')
                    navigate('/goal-setting')
                  }}
                >
                  <Icon icon={regular('bullseye')} style={{ marginRight: 8 }} />
                  Set Goal
                </Button>
              )}
              <br />
              <Link
                to="/insights/profit"
                trackMessage="Clicked year to date profit"
                style={{ justifyContent: 'flex-start' }}
              >
                View profit breakdown
                <Icon icon={light('arrow-right')} style={{ marginLeft: 8 }} />
              </Link>
            </Card>
          </Grid.Column>
        </Grid.Row>
        <GridRowColumn>
          <Accordion
            variant="text"
            title="Month over Month Profit and Loss Chart"
            content={<InsightsTrendGraph data={trendData} />}
            onOpenClick={(isOpen) => {
              track(
                isOpen
                  ? 'Expanded month-over-month P&L chart'
                  : 'Collapsed month-over-month P&L chart'
              )
            }}
          />
        </GridRowColumn>
      </Grid>
    </Card>
  )
}

export default YearToDateProfitLossCard
